//src/components/sections/section-featured-grid.tsx
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import CardCarouselNews from 'src/components/cards/card-carousel-news';
import CardMainNews from 'src/components/cards/card-main-news';
import Carousels from 'src/components/carousels';
import WidgetHeader from 'src/components/widgets/widget-header';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import useResponsive from 'src/utils/device';

type Props = {
  data: any[];
  title?: string;
  link?: string;
};

const SectionFeaturedGrid = ({ data = [], title = 'FEATURED', link }: Props) => {
  const router = useRouter();

  if (isEmpty(data)) return null;

  const responsive = useResponsive();
  const carouselData = data.slice(0, 4);

  //กรณีมีข้อมูลครบ 4 ใช้ layout เดิม (left large, center large, right 2 small)
  const hasFull = data.length >= 4;
  const leftCard = hasFull ? data[0] : null;
  const centerCard = hasFull ? data[1] : null;
  const rightCards = hasFull ? data.slice(2, 4) : [];

  return (
    <SectionWrapper>
      {title && <WidgetHeader title={title} as='h2' titleColor='#ffffff' variant='line' position={!responsive.isMobile ? 'left' : 'center'} showViewMore={!responsive.isMobile} viewMoreLink={link} />}
      <div className='container'>
        {!responsive.isMobile ? (
          hasFull ? (
            /*Desktop View - Full 4 items: layout เดิม */
            <div className='grid-wrapper'>
              <div className='grid-left'>
                {leftCard && (
                  <CardMainNews
                    data={leftCard}
                    showTitle={false}
                    showCategory={false}
                    aspectRatio='4/13'
                    type='CARD_MEDIUM'
                    vertical_image={true}
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-4',
                      position: 'ใต้ : section-3',
                      data: {
                        title: leftCard?.title,
                        heading: title,
                        index: 1
                      }
                    }}
                  />
                )}
              </div>
              <div className='grid-center'>
                {centerCard && (
                  <CardMainNews
                    data={centerCard}
                    showTitle={false}
                    showCategory={false}
                    aspectRatio='3/4'
                    vertical_image={true}
                    type='CARD_MEDIUM'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-4',
                      position: 'ใต้ : section-3',
                      data: {
                        title: centerCard?.title,
                        heading: title,
                        index: 2
                      }
                    }}
                  />
                )}
              </div>
              <div className='grid-right'>
                {rightCards.map((item: any, index: number) => (
                  <CardMainNews
                    key={item?.id || index}
                    data={item}
                    showTitle={false}
                    showCategory={false}
                    vertical_image={true}
                    type='CARD_SMALL'
                    aspectRatio='3/4'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-4',
                      position: 'ใต้ : section-3',
                      data: {
                        title: item?.title,
                        heading: title,
                        index: index + 3
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            /*Desktop View - น้อยกว่า 4 items: แสดง equal columns, 3:4 ทุกอัน */
            <div className='grid-equal'>
              {data.map((item: any, index: number) => (
                <div key={item?.id || index} className='grid-equal-item'>
                  <CardMainNews
                    data={item}
                    showTitle={false}
                    showCategory={false}
                    aspectRatio='3/4'
                    vertical_image={true}
                    type='CARD_MEDIUM'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-4',
                      position: 'ใต้ : section-3',
                      data: {
                        title: item?.title,
                        heading: title,
                        index: index + 1
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          <div className='carousel-wrapper'>
            <Carousels infinite={true} mobileSlideShowTwo={1} pauseOnHover={false} autoplaySpeed={6000} arrows={true}>
              {carouselData.map((item: any, index: number) => (
                <div key={item?.id || index} className='card-items'>
                  <CardCarouselNews
                    data={item}
                    showTitle={true}
                    showCategory={true}
                    vertical_image={true}
                    type='CARD_MEDIUM'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-4',
                      position: 'ใต้ : section-3',
                      data: {
                        title: item?.title,
                        heading: title,
                        carousel: index + 1
                      }
                    }}
                  />
                </div>
              ))}
            </Carousels>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

const SectionWrapper = styled.section`
  background-color: #b00022;
  padding: 40px 0;

  .title-link {
    background-color: #b00022 !important;
    padding: 0 20px 0 !important;
    .title {
      font-size: 45px !important;
      @media (max-width: 768px) {
        font-size: 30px !important;
        font-weight: 400 !important;
        letter-spacing: 5px !important;
      }
    }
    @media (max-width: 768px) {
      padding: 0 5px 0 !important;
    }
  }
  .variant-line .full-width-line {
    background-color: #ffffff !important;
    top: 40%;
    @media (max-width: 768px) {
      display: none;
    }
  }
  .variant-line {
    padding: 10px 0 30px;
    .view-more {
      background-color: #b00022 !important;
      color: #ffffff !important;
    }
  }

  /* ========== Desktop Grid - Full 4 items ========== */
  .grid-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 0.6fr;
    gap: 15px;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr 1fr;
      height: auto;

      .grid-right {
        grid-column: span 2;
        flex-direction: row;
      }
    }
  }

  .grid-left,
  .grid-center {
    height: 100%;

    .card-main-news {
      height: 100%;
      border-radius: 0px !important;
      a {
        height: 100%;
      }
      .card-thumb {
        height: 100%;
        aspect-ratio: unset;
      }
    }
  }

  .grid-right {
    display: flex;
    flex-direction: column;
    gap: 15px;
    height: 100%;

    .card-main-news {
      flex: 1;
      height: calc(50% - 7.5px);
      border-radius: 0px !important;
      .card-thumb {
        height: 100%;
      }
    }

    @media (max-width: 1024px) {
      .card-main-news {
        height: auto;

        .card-thumb {
          aspect-ratio: 9/16;
        }
      }
    }
  }

  /* ========== Desktop Grid - น้อยกว่า 4 items (equal columns, 3:4) ========== */
  .grid-equal {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;

    .grid-equal-item {
      .card-main-news {
        border-radius: 0px !important;
        .card-thumb {
          aspect-ratio: 3 / 4;
        }
      }
    }
  }

  .card-thumb .resolution-image {
    width: 100%;
    height: 100%;
  }

  /* ========== Mobile Carousel ========== */
  .carousel-wrapper {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    min-width: 0;

    .carousel {
      .slick-slider {
        .slick-list {
          overflow: hidden;
        }

        .slick-prev,
        .slick-next {
          &:before {
            color: #fff;
          }
        }
      }

      .slick-dots {
        li button:before {
          background-color: rgba(255, 255, 255, 0.5);
        }
        li.slick-active button:before {
          background-color: #fff;
        }
      }
    }

    .card-carousel-news:hover {
      .title {
        color: #ffffff;
      }
    }

    .card-items {
      padding: 0 5px;
      .card-thumb {
        aspect-ratio: 3 / 3.5;
      }
    }
  }
`;

export default SectionFeaturedGrid;
